import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'
import { API_BASE_URL, isNative, mobileConfig } from '@/config/mobile'

// Dynamic API URL based on current hostname and platform
const getApiBaseUrl = () => {
  // For native mobile apps (Capacitor), use the configured API URL
  if (isNative) {
    console.log('📱 Running on native platform, using:', API_BASE_URL)
    return API_BASE_URL
  }

  // In dev mode, use relative path to go through Vite proxy
  if (import.meta.env.DEV) {
    return '/api'
  }

  // If VITE_API_URL is set, use it
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }

  // Otherwise, use current hostname with port 5000
  const hostname = window.location.hostname
  const protocol = window.location.protocol
  return `${protocol}//${hostname}:5000/api`
}

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: isNative ? mobileConfig.apiTimeout : 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

console.log('API Base URL:', getApiBaseUrl())

// console.log('API Base URL:', import.meta.env.VITE_API_URL || 'http://localhost:5000/api')

let isRefreshing = false
let failedQueue = []

// Request deduplication - prevent duplicate requests
const pendingRequests = new Map()

const getRequestKey = (config) => {
  return `${config.method}-${config.url}-${JSON.stringify(config.params || {})}`
}

const addPendingRequest = (config) => {
  const requestKey = getRequestKey(config)

  // If there's already a pending request, return that promise
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey)
  }

  // Create a new promise for this request
  const promise = new Promise((resolve, reject) => {
    config._resolve = resolve
    config._reject = reject
  })

  pendingRequests.set(requestKey, promise)
  return null // Signal that this is a new request
}

const removePendingRequest = (config) => {
  const requestKey = getRequestKey(config)
  pendingRequests.delete(requestKey)
}

const resolvePendingRequests = (config, response) => {
  const requestKey = getRequestKey(config)
  const promise = pendingRequests.get(requestKey)

  if (promise && config._resolve) {
    config._resolve(response)
  }

  removePendingRequest(config)
}

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

api.interceptors.request.use(
  (config) => {
    // Check for duplicate requests (only for GET requests)
    if (config.method === 'get') {
      const existingRequest = addPendingRequest(config)
      if (existingRequest) {
        // Return existing promise instead of making a new request
        console.log(`🔄 Reusing pending request: ${getRequestKey(config)}`)
        return existingRequest.then(() => {
          // This will never execute, but prevents the request
          return Promise.reject({ __CANCEL__: true })
        })
      }
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const authStore = useAuthStore()
    if (authStore.branchId) {
      config.headers['X-Branch-ID'] = authStore.branchId
    }

    // Add admin token if available
    const adminToken = localStorage.getItem('adminToken')
    if (adminToken) {
      config.headers['X-Admin-Token'] = adminToken
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    // Resolve any pending duplicate requests
    if (response.config.method === 'get') {
      resolvePendingRequests(response.config, response)
    }
    return response
  },
  async (error) => {
    // Handle cancelled duplicate requests
    if (error.__CANCEL__) {
      return Promise.reject({ __CANCEL__: true, silent: true })
    }

    const originalRequest = error.config

    // Clean up pending request on error
    if (originalRequest && originalRequest.method === 'get') {
      removePendingRequest(originalRequest)
    }

    // Handle 429 Too Many Requests with exponential backoff
    if (error.response?.status === 429 && !originalRequest._retryCount) {
      originalRequest._retryCount = 0
    }

    if (error.response?.status === 429 && originalRequest._retryCount < 5) {
      originalRequest._retryCount++
      const delay = Math.min(2000 * Math.pow(2, originalRequest._retryCount - 1), 16000)

      console.log(`⏳ Rate limited (429). Retrying in ${delay}ms... (attempt ${originalRequest._retryCount}/5)`)

      await new Promise(resolve => setTimeout(resolve, delay))

      // Remove from pending requests before retry
      if (originalRequest.method === 'get') {
        removePendingRequest(originalRequest)
      }

      return api(originalRequest)
    }

    // Handle 401 Unauthorized (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh for login/refresh endpoints (would cause infinite loop)
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const authStore = useAuthStore()

      try {
        const success = await authStore.refreshAuthToken()
        if (success) {
          processQueue(null, authStore.token)
          originalRequest.headers.Authorization = `Bearer ${authStore.token}`
          return api(originalRequest)
        } else {
          throw new Error('Token refresh failed')
        }
      } catch (refreshError) {
        processQueue(refreshError, null)
        // Don't logout on 429 errors during refresh
        if (refreshError.response?.status !== 429) {
          authStore.logout()
          router.push('/login')
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    if (error.response?.status >= 500) {
      console.error('Server Error:', error.response.data)
    }

    return Promise.reject(error)
  }
)

const retryRequest = async (fn, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn()
    } catch (error) {
      if (i === retries - 1) throw error
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)))
    }
  }
}

export { retryRequest }
export default api